import React from 'react';
import { InputProps, Input as TUIInput } from '@apia/theme';
import validationsStore from '../../../../store/validation/validationStore';
import DateInput from '../../fields/DateInput';
import { FormContext } from '../FormContext';
import useField from '../hooks/useField';
import useFieldStatesClassNames from '../hooks/useFieldStyles';
import { TDefaultFieldType } from './types';
import useValidationClass from './useValidationClass';
import Wrapper from './Wrapper';

type TInput = InputProps & TDefaultFieldType<string>;

const Input = React.forwardRef<HTMLInputElement, TInput>(
  (
    {
      avoidSemicolon,
      className,
      label,
      onChange: outerOnChange,
      type,
      /**
       * useField
       */
      avoidRegisterIfExists,
      formName: propsFormName,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      value,
      ...props
    },
    ref,
  ) => {
    const { name: contextFormName } = React.useContext(FormContext);
    const formName = propsFormName ?? contextFormName;

    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        validationClass,
        validationFunction,
        validationRules,
      });

    const {
      error,
      updateValidations,
      onChange,
      isTouched,
      ...validationProps
    } = useField<string>({
      formName,
      name,
      initialValue:
        initialValue ??
        (value as string) ??
        validationsStore.getFieldStore(formName, name)?.value ??
        '',
      removeOnUnmount,
      submitValueParser,
      validationFunction: actualValidationFunction,
      validationRules: actualValidationRules,
    });

    const handleOnChange = React.useCallback(
      (ev: string | React.ChangeEvent<HTMLInputElement>) => {
        onChange(ev);
        if (typeof ev === 'string') {
          // Es date
          outerOnChange?.({
            target: ref ? ref : { value: ev },
          } as unknown as React.ChangeEvent<HTMLInputElement>);
        } else {
          // Es comun
          outerOnChange?.(ev);
        }
      },
      [onChange, outerOnChange, ref],
    );

    const stateClassName = useFieldStatesClassNames(name);

    return (
      <Wrapper
        name={name}
        label={label}
        className={`input ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
      >
        {type === 'date' ? (
          <DateInput
            {...props}
            {...validationProps}
            aria-label={props['aria-label'] ?? label}
            onError={undefined}
            ref={ref}
            onChange={handleOnChange}
            onBlur={handleOnChange}
            value={validationProps.value ?? ''}
            inputClassName={stateClassName}
          />
        ) : (
          <TUIInput
            type={type}
            {...props}
            {...validationProps}
            aria-label={props['aria-label'] ?? label}
            ref={ref}
            onChange={handleOnChange}
            value={validationProps.value ?? ''}
            className={stateClassName}
          />
        )}
      </Wrapper>
    );
  },
);

export default React.memo(Input);
